import React, { useState } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAdminLiveSummaryQuery,
  useGetAdminActiveIndividualSimulationsQuery,
  useGetAdminActiveTeamSessionsQuery,
  type ActiveIndividualSimulation,
  type ActiveTeamSession,
} from "../../app/services/meseeknaApiV2"
import { HiOutlineUserGroup, HiOutlineOfficeBuilding } from "react-icons/hi"
import { BiPulse, BiRefresh, BiTime } from "react-icons/bi"
import { FiUsers, FiActivity, FiClock, FiMail } from "react-icons/fi"
import { MdOutlineTimer } from "react-icons/md"

// Icon wrapper components to fix TypeScript compatibility with react-icons
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserGroupIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineUserGroup {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const OfficeBuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const PulseIcon = (props: { size?: number; className?: string }): JSX.Element => <BiPulse {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const RefreshIcon = (props: { size?: number; className?: string }): JSX.Element => <BiRefresh {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TimeIcon = (props: { size?: number; className?: string }): JSX.Element => <BiTime {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UsersIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUsers {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ActivityIcon = (props: { size?: number; className?: string }): JSX.Element => <FiActivity {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ClockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiClock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const MailIcon = (props: { size?: number; className?: string }): JSX.Element => <FiMail {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TimerIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineTimer {...props} />

type TabType = "individual" | "team"

const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleString()
}

const IndividualSimulationCard: React.FC<{
  simulation: ActiveIndividualSimulation
}> = ({ simulation }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <UsersIcon className="text-blue-600" size={18} />
        </div>
        <div>
          <p className="font-medium text-gray-900">
            {simulation.userFirstName || simulation.userLastName
              ? `${simulation.userFirstName || ""} ${simulation.userLastName || ""}`.trim()
              : "Unknown User"}
          </p>
          <p className="text-sm text-gray-500">{simulation.userEmail}</p>
        </div>
      </div>
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
        Active
      </span>
    </div>

    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <ActivityIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">
          {simulation.testTitle || simulation.testShortName || "Unknown Simulation"}
        </span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <OfficeBuildingIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">{simulation.organizationName}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <TimerIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">
          Started: {formatDate(simulation.startTime)}
        </span>
      </div>
    </div>

    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
      <div className="flex items-center gap-1 text-sm text-amber-600">
        <TimeIcon size={16} />
        <span className="font-medium">
          {formatDuration(simulation.durationMinutes)} elapsed
        </span>
      </div>
    </div>
  </div>
)

const TeamSessionCard: React.FC<{
  session: ActiveTeamSession
}> = ({ session }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
          <UserGroupIcon className="text-purple-600" size={18} />
        </div>
        <div>
          <p className="font-medium text-gray-900">{session.name}</p>
          <p className="text-sm text-gray-500">
            {session.scenarioName || "Unknown Scenario"}
          </p>
        </div>
      </div>
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          session.status === "active"
            ? "bg-green-100 text-green-800"
            : "bg-amber-100 text-amber-800"
        }`}
      >
        {session.status === "active" && (
          <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
        )}
        {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
      </span>
    </div>

    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <OfficeBuildingIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">{session.organizationName}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <UsersIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">
          {session.participantCount} participant
          {session.participantCount !== 1 ? "s" : ""}
        </span>
      </div>
      {session.createdByEmail && (
        <div className="flex items-center gap-2 text-sm">
          <MailIcon className="text-gray-400" size={14} />
          <span className="text-gray-600">{session.createdByEmail}</span>
        </div>
      )}
      <div className="flex items-center gap-2 text-sm">
        <ClockIcon className="text-gray-400" size={14} />
        <span className="text-gray-600">
          Created: {formatDate(session.createdAt)}
        </span>
      </div>
    </div>
  </div>
)

const LiveActivityPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("individual")

  const { data: liveSummary, isLoading: summaryLoading, refetch: refetchSummary } =
    useGetAdminLiveSummaryQuery(undefined, {
      pollingInterval: 30000,
    })

  const {
    data: individualData,
    isLoading: individualLoading,
    refetch: refetchIndividual,
  } = useGetAdminActiveIndividualSimulationsQuery(undefined, {
    pollingInterval: 30000,
  })

  const {
    data: teamData,
    isLoading: teamLoading,
    refetch: refetchTeam,
  } = useGetAdminActiveTeamSessionsQuery(undefined, {
    pollingInterval: 30000,
  })

  const summary = liveSummary?.data
  const individualSimulations = individualData?.data || []
  const teamSessions = teamData?.data || []

  const handleRefresh = () => {
    refetchSummary()
    refetchIndividual()
    refetchTeam()
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <PulseIcon className="text-green-500" />
                  Live Activity Monitor
                </h1>
                <p className="text-gray-500 mt-1">
                  Real-time view of all active simulations
                </p>
              </div>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshIcon size={18} />
                Refresh
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div
                className={`rounded-lg border p-4 cursor-pointer transition-all ${
                  activeTab === "individual"
                    ? "bg-blue-50 border-blue-200 ring-2 ring-blue-200"
                    : "bg-white border-gray-200 hover:border-blue-200"
                }`}
                onClick={() => setActiveTab("individual")}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      activeTab === "individual"
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <UsersIcon size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {summaryLoading ? "-" : summary?.individualActive || 0}
                    </p>
                    <p className="text-sm text-gray-500">Individual Sims</p>
                  </div>
                  {!summaryLoading && (summary?.individualActive || 0) > 0 && (
                    <div className="ml-auto">
                      <span className="w-2 h-2 bg-green-500 rounded-full inline-block animate-pulse"></span>
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`rounded-lg border p-4 cursor-pointer transition-all ${
                  activeTab === "team"
                    ? "bg-purple-50 border-purple-200 ring-2 ring-purple-200"
                    : "bg-white border-gray-200 hover:border-purple-200"
                }`}
                onClick={() => setActiveTab("team")}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      activeTab === "team"
                        ? "bg-purple-100 text-purple-600"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <UserGroupIcon size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {summaryLoading ? "-" : summary?.teamSessionsActive || 0}
                    </p>
                    <p className="text-sm text-gray-500">Team Sessions</p>
                  </div>
                  {!summaryLoading && (summary?.teamSessionsActive || 0) > 0 && (
                    <div className="ml-auto">
                      <span className="w-2 h-2 bg-green-500 rounded-full inline-block animate-pulse"></span>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border bg-white border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gray-100 text-gray-500">
                    <ActivityIcon size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {summaryLoading
                        ? "-"
                        : summary?.collaborationRoomsActive || 0}
                    </p>
                    <p className="text-sm text-gray-500">Collab Rooms</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border bg-white border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                    <PulseIcon size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {summaryLoading ? "-" : summary?.completedToday || 0}
                    </p>
                    <p className="text-sm text-gray-500">Completed Today</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === "individual" && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Active Individual Simulations
                </h2>
                {individualLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                          <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2 mt-1"></div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : individualSimulations.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {individualSimulations.map((sim) => (
                      <IndividualSimulationCard key={sim.id} simulation={sim} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <UsersIcon size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">
                      No Active Individual Simulations
                    </h3>
                    <p className="text-gray-500 mt-1">
                      There are currently no users taking individual simulations.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "team" && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Active Team Sessions
                </h2>
                {teamLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                          <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2 mt-1"></div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : teamSessions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teamSessions.map((session) => (
                      <TeamSessionCard key={session.id} session={session} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <UserGroupIcon
                      size={48}
                      className="mx-auto text-gray-300 mb-4"
                    />
                    <h3 className="text-lg font-medium text-gray-900">
                      No Active Team Sessions
                    </h3>
                    <p className="text-gray-500 mt-1">
                      There are currently no active team simulation sessions.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default LiveActivityPage
